import { type CustomerAgeingData } from "@/types/ageing-analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  CreditCard,
  PoundSterling,
  Clock,
  Phone,
  Mail,
  FileText,
  CheckCircle2,
  MessageSquare,
  Banknote,
  FileWarning,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

interface CustomerOverviewTabProps {
  customer: CustomerAgeingData;
  formatCurrency: (amount: number) => string;
}

export function CustomerOverviewTab({ customer, formatCurrency }: Readonly<CustomerOverviewTabProps>) {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case "call":
        return <Phone className="h-4 w-4" />;
      case "email":
        return <Mail className="h-4 w-4" />;
      case "payment":
        return <Banknote className="h-4 w-4" />;
      case "promise":
        return <CheckCircle2 className="h-4 w-4" />;
      case "note":
        return <MessageSquare className="h-4 w-4" />;
      case "dispute":
        return <FileWarning className="h-4 w-4" />;
      case "letter":
        return <FileText className="h-4 w-4" />;
      case "meeting":
        return <Calendar className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case "call":
        return "bg-blue-100 text-blue-700";
      case "email":
        return "bg-purple-100 text-purple-700";
      case "payment":
        return "bg-green-100 text-green-700";
      case "promise":
        return "bg-cyan-100 text-cyan-700";
      case "note":
        return "bg-gray-100 text-gray-700";
      case "dispute":
        return "bg-red-100 text-red-700";
      case "letter":
        return "bg-orange-100 text-orange-700";
      case "meeting":
        return "bg-indigo-100 text-indigo-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <ScrollArea className="h-full">
      <ScrollAreaViewport className="h-full">
        <div className="px-6 py-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Outstanding</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrency(customer.totalOutstanding)}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-red-100">
                    <PoundSterling className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Credit Limit</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrency(customer.creditLimit)}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-blue-100">
                    <CreditCard className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Days Overdue</p>
                    <p className="text-2xl font-bold mt-1">{customer.averageDaysOverdue}</p>
                  </div>
                  <div className="p-3 rounded-full bg-orange-100">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Last Contact</p>
                    <p className="text-sm font-semibold mt-1">
                      {formatDistanceToNow(new Date(customer.lastContactDate), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-purple-100">
                    <Calendar className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity and Performance Metrics Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Recent Activity Timeline */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {customer.recentActivity && customer.recentActivity.length > 0 ? (
                  <div className="space-y-4">
                    {customer.recentActivity.slice(0, 5).map((activity, index) => (
                      <div key={activity.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`p-2 rounded-full ${getActivityColor(activity.type)}`}>
                            {getActivityIcon(activity.type)}
                          </div>
                          {index < (customer.recentActivity?.length ?? 0) - 1 && index < 4 && (
                            <div className="w-px h-8 bg-border mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium capitalize">{activity.type}</p>
                              <p className="text-sm text-muted-foreground mt-0.5">{activity.summary}</p>
                              {activity.outcome && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Outcome: {activity.outcome}
                                </p>
                              )}
                              {activity.amount != null && (
                                <p className="text-sm font-semibold text-green-600 mt-1">
                                  {formatCurrency(activity.amount)}
                                </p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(activity.date), "MMM dd")}
                              </p>
                              {activity.user && (
                                <p className="text-xs text-muted-foreground mt-0.5">{activity.user}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No recent activity recorded</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            {customer.metrics && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Avg Payment Days</p>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">{customer.metrics.averagePaymentDays}</p>
                        {customer.metrics.averagePaymentDays <= 30 ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Payment Reliability</p>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">{customer.metrics.paymentReliabilityScore}%</p>
                        <Progress value={customer.metrics.paymentReliabilityScore} className="h-2 flex-1" />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Overdue Invoices</p>
                      <p className="text-lg font-semibold">{customer.metrics.totalInvoicesOverdue}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Collection Strategy</p>
                      <Badge variant="outline" className="mt-1">{customer.collectionStrategy}</Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Terms</p>
                      <p className="text-lg font-semibold">{customer.terms}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar orientation="vertical" />
    </ScrollArea>
  );
}
